import React, { useEffect, useState } from "react"
import * as url from "../../helpers/url_helper"

import {
    Alert,
    Button,
    Card,
    CardBody,
    Col,
    Form,
    FormFeedback,
    FormGroup,
    Input,
    Label,
    Row,
} from "reactstrap"

import { useFormik } from "formik"
import {
    convertFileToBase64,
    formatBytes,
    validateImageFile,
} from "helpers/file_helper"
import { validateBeforeConfirm } from "helpers/validation_helper"
import Dropzone from "react-dropzone"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { addNewBrand } from "store/actions"
import {
    getBrandDetail as onGetBrandDetail,
    clearBrandDetail,
} from "store/brands/actions"
import * as Yup from "yup"
import ConfirmButton from "components/Common/ConfirmButton"
import AccessControlWrapper from "components/Common/PermissionButton"
import TextInput from "components/Common/TextInput"
import TextArea from "components/Common/TextArea"
import CButton from "components/Common/CButton"
import IconButton from "components/Common/IconButton"

export default function BrandForm({ page, match, history }) {
    const [selectedFiles, setselectedFiles] = useState([])
    const [readOnly, setReadOnly] = useState(true)
    const [hasFormChanged, setHasFormChanged] = useState(false)

    const dispatch = useDispatch()

    const brandID = match.params.id
    const { brandDetail } = useSelector(state => state.brands)
    const brand =
        page === "add"
            ? null
            : brandDetail && Array.isArray(brandDetail)
                ? brandDetail.find(b => b.brandID === brandID)
                : brandDetail

    useEffect(() => {
        if (page === "add") {
            setReadOnly(false)
            // Clear any existing brand detail when adding new brand
            dispatch(clearBrandDetail())
        } else {
            setReadOnly(true)
            if (
                !brand ||
                (brand &&
                    Array.isArray(brand) &&
                    !brand.find(b => b.brandID === brandID))
            ) {
                // Check for updated parameter in URL to bust cache
                const urlParams = new URLSearchParams(window.location.search)
                const isUpdated = urlParams.get('updated') === 'true'

                const options = {
                    headers: {
                        "input-parameters": { brandIDs: [brandID] },
                    },
                }

                // Add timestamp as URL query parameter for cache busting if updated parameter is present
                if (isUpdated) {
                    options.params = { updated: Date.now() }
                }

                dispatch(onGetBrandDetail(options))
            }
        }
    }, [dispatch, brand, brandID, page])

    // Clear brand detail when component unmounts or brandID changes
    useEffect(() => {
        return () => {
            dispatch(clearBrandDetail())
        }
    }, [dispatch, brandID])

    useEffect(() => {
        if (brand && brand.logo) {
            setselectedFiles([
                {
                    name: brand.name || "",
                    preview: brand.logo,
                    isExisting: true,
                },
            ])
        }
    }, [brand])

    async function handleAcceptedFiles(files) {
        if (files.length > 0) {
            const file = files[0]

            const base64Img = await convertFileToBase64(file)

            try {
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                    formattedSize: formatBytes(file.size),
                    base64Img,
                    isExisting: false,
                })
                setselectedFiles([file])
                validation.setFieldValue("logo", file)
            } catch (error) {
                console.error("Error converting to base64:", error)
            }
        }
    }

    // Form validation
    const validation = useFormik({
        enableReinitialize: true,

        initialValues: {
            name: brand ? brand.name : "",
            description: brand ? brand.description : "",
            logo: brand
                ? {
                    name: brand.name || "",
                    preview: brand.logo,
                    isExisting: true,
                }
                : "",
            brandID: brand ? brand.brandID : "",
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .required("Please Enter Brand Name")
                .max(255, "Brand Name cannot be greater than 255"),

            description: Yup.string()
                .required("Please Enter Brand Description")
                .max(510, "Brand Description cannot be greater than 510"),

            logo: Yup.mixed()
                .nullable()
                .when([], {
                    is: () => page === "add",
                    then: schema => schema.required("Please Upload Brand Logo"),
                    otherwise: schema => schema,
                })
                .test("brandImage", "Invalid brand image", async function (value) {
                    if (!value && page !== "add") {
                        return true
                    }

                    if (value && value.isExisting === true) {
                        return true
                    }

                    try {
                        await validateImageFile(value, "brand", 550, 550, 1) // 550x550, 1MB
                        return true
                    } catch (error) {
                        return this.createError({ message: error.message })
                    }
                }),
        }),
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleConfirmedSubmit()
            }
        },
    })

    // Function to handle the actual form submission after confirmation
    const handleConfirmedSubmit = () => {
        const values = validation.values

        const payload = {
            ...values,
            actionType: page === "add" ? "create" : "update",
            logo: values.logo["base64Img"] || "",
        }
        const options = {
            // encDecAlgo: "BASE64",
        }

        dispatch(addNewBrand(payload, options, history))
    }

    const clearLogo = () => {
        setselectedFiles([])
        validation.setFieldValue("logo", "")
        if (!hasFormChanged && page === "update") {
            setHasFormChanged(true)
        }
    }

    const { brandError, isUpdateSuccess } = useSelector(state => ({
        brandError: state.brands.error.message || state.brands.error.description,
        isUpdateSuccess: state.brands.isUpdateSuccess,
    }))

    // Reset form to readonly after successful update
    useEffect(() => {
        if (isUpdateSuccess && page === "update") {
            setReadOnly(true)
            setHasFormChanged(false)

            // Add updated=true parameter to URL for cache-busting on next load
            const currentUrl = new URL(window.location)
            currentUrl.searchParams.set('updated', 'true')
            history.replace(currentUrl.pathname + currentUrl.search)
        }
    }, [isUpdateSuccess, page, history])

    const handleChange = e => {
        validation.handleChange(e)
        if (!hasFormChanged && page === "update") {
            setHasFormChanged(true)
        }
    }

    return (
        <>
            {page !== "add" && readOnly && (
                <div className="mb-3 d-flex justify-content-end">
                    <AccessControlWrapper
                        actionType={"post"}
                        contextUri={url.MANAGE_BRAND}
                    >
                        <CButton.Primary
                            text="Edit Brand"
                            onClick={() => setReadOnly(false)}
                        />
                    </AccessControlWrapper>
                </div>
            )}

            {brandError && (
                <Alert color="danger" className="mb-3">
                    {brandError}
                </Alert>
            )}

            <Card>
                <CardBody>
                    <Form
                        className="needs-validation"
                        onSubmit={e => {
                            e.preventDefault()
                            validation.handleSubmit()
                            return false
                        }}
                    >
                        <Row>
                            <Col md="12">
                                <TextInput
                                    label="Brand Name"
                                    name="name"
                                    placeholder="Enter Brand Name"
                                    type="text"
                                    helperText={"Max 255 chars allowed"}
                                    maxLength={255}
                                    readOnly={readOnly}
                                    required={true}
                                    validation={validation}
                                    handleChange={handleChange}
                                />
                            </Col>
                            <Col md="12">
                                <div>
                                    <TextArea
                                        label="Description"
                                        name="description"
                                        placeholder="Enter Brand Description"
                                        type="textarea"
                                        helperText={"Max 255 chars allowed"}
                                        maxLength={255}
                                        readOnly={readOnly}
                                        required={true}
                                        validation={validation}
                                        handleChange={handleChange}
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="12">
                                <FormGroup style={{ marginBottom: "0 !important" }}>
                                    {selectedFiles.length === 0 || validation.errors.logo ? (
                                        <>
                                            <Dropzone
                                                multiple={false}
                                                accept={{
                                                    "image/*": [".png", ".jpeg", ".jpg"],
                                                }}
                                                onDrop={acceptedFiles => {
                                                    handleAcceptedFiles(acceptedFiles)
                                                }}
                                            >
                                                {({ getRootProps, getInputProps }) => (
                                                    <div className="dropzone">
                                                        <div
                                                            className="dz-message needsclick mt-2"
                                                            {...getRootProps()}
                                                        >
                                                            <input {...getInputProps()} />
                                                            <div className="mb-3">
                                                                <i className="display-4 text-muted bx bxs-cloud-upload" />
                                                            </div>
                                                            <h4>Drop brand image here or click to upload.</h4>
                                                        </div>
                                                    </div>
                                                )}
                                            </Dropzone>
                                        </>
                                    ) : null}

                                    {selectedFiles.length > 0 && !validation.errors.logo ? (
                                        <div className="dropzone-previews mt-3" id="file-previews">
                                            {selectedFiles.map((f, i) => {
                                                return (
                                                    <Card
                                                        className="d-inline-block mt-1 mb-2 position-relative shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                                        key={i + "-file"}
                                                    >
                                                        <div className="p-2">
                                                            {readOnly ? null : (
                                                                <IconButton
                                                                    icon="bx bxs-x-circle"
                                                                    variant="rounded-circle"
                                                                    className="position-absolute right-0 top-0 start-100 translate-middle"
                                                                    style={{
                                                                        backgroundColor: "transparent",
                                                                        border: "none",
                                                                        fontSize: "1.5rem",
                                                                        color: "#f46a6a",
                                                                    }}
                                                                    onClick={clearLogo}
                                                                    title={"Clear Logo "}
                                                                />
                                                            )}

                                                            <div>
                                                                <div className="text-center mb-2">
                                                                    <img
                                                                        data-dz-thumbnail=""
                                                                        height="200"
                                                                        className="rounded bg-light"
                                                                        alt={f.name}
                                                                        src={f.preview}
                                                                    />
                                                                </div>
                                                                {
                                                                    f.name && f.formattedSize ? (
                                                                        <div className="text-center">
                                                                            <Link
                                                                                to="#"
                                                                                className="text-muted font-weight-bold"
                                                                            >
                                                                                {f.name}
                                                                            </Link>
                                                                            <p className="mb-0">
                                                                                <strong>{f.formattedSize}</strong>
                                                                            </p>
                                                                        </div>) : null
                                                                }
                                                            </div>
                                                        </div>
                                                    </Card>
                                                )
                                            })}
                                        </div>
                                    ) : null}

                                    {validation.errors.logo ? (
                                        <small
                                            className={`invalid-feedback d-inline-block align-top ${validation.touched.logo && validation.errors.logo
                                                ? "w-50"
                                                : "w-100"
                                                }`}
                                        >
                                            {validation.errors.logo}
                                        </small>
                                    ) : null}

                                    {selectedFiles.length === 0 || validation.errors.logo ? (
                                        <span
                                            className={`"text-muted text-end ${readOnly ? "d-none" : "d-inline-block"
                                                } ${validation.touched.logo && validation.errors.logo
                                                    ? "w-50"
                                                    : "w-100"
                                                }`}
                                        >
                                            jpg & png are allowed upto 1mb max (550x550)
                                        </span>
                                    ) : null}
                                </FormGroup>
                            </Col>
                        </Row>

                        {page === "update" && readOnly ? null : (
                            <div className="d-flex gap-2 justify-content-end">
                                {page === "add" ? (
                                    <ConfirmButton
                                        onConfirm={() => history.push("/brands")}
                                        message="Are you sure you want to cancel? Any unsaved changes will be lost."
                                        renderButton={<CButton.Secondary text="Cancel" />}
                                    />
                                ) : (
                                    <CButton.Secondary
                                        text="Cancel"
                                        onClick={() => {
                                            setReadOnly(true)
                                            setHasFormChanged(false)
                                            validation.resetForm()
                                        }}
                                    />
                                )}

                                <ConfirmButton
                                    validateBeforeConfirm={() =>
                                        validateBeforeConfirm(validation)
                                    }
                                    onConfirm={handleConfirmedSubmit}
                                    message={
                                        page === "add"
                                            ? "Are you sure you want to create this brand? Please review all details before proceeding."
                                            : "Are you sure you want to update this brand? This action will modified the existing brand information."
                                    }
                                    disabled={page === "update" && !hasFormChanged}
                                    actionType={page === "add" ? "post" : ""}
                                    contextUri={page === "add" ? url.MANAGE_BRAND : ""}
                                    renderButton={
                                        <CButton.Primary
                                            text={page === "add" ? "Create Brand" : "Update"}
                                            disabled={page === "update" && !hasFormChanged}
                                        />
                                    }
                                />
                            </div>
                        )}
                    </Form>
                </CardBody>
            </Card>
        </>
    )
}
